import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { History } from '@o2d/history';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Konva from 'konva';
import { Editor } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

import type { WhiteboardBus } from '../../core/bus/whiteboard-bus.js';
import type { UiAction } from '../../core/bus/ui-action.events.js';
import { objectId, type ObjectId } from '../../core/bus/branded-types.js';
import type { BoardState } from '../../core/state/board-state.js';
import { UpdatePropertyCommand } from '../../core/command/update-property.command.js';
import { isTextBox } from './textbox.types.js';
import type { TextBox } from './textbox.interface.js';
import { deriveAdvisoryListType } from './text-document-migration.js';
import { SetListTypeCommand } from './textbox.commands.js';
import {
    buildTextboxEditContentElement,
    LINK_ICON_ATTR,
    LINK_ICON_GAP,
    LINK_ICON_GLYPH,
    LINK_ICON_SCALE,
    VALIGN_LAYOUT,
} from './textbox-html-builder.js';
import {
    parseFontSizePx,
    maxRunFontSizePxFromJson,
    firstRunFontSizePxFromJson,
    type StrutJsonNode,
} from './textbox-strut-fix.js';
import type { TextBoxGroup } from './textbox.renderer.js';
import { EDIT_TEXTBOX_EXTENSIONS } from './tiptap-extensions.js';
import { handleTextboxPaste } from './paste-format.js';
import { autolinkDocument } from './autolink-document.js';
import type { TextDocument } from './text-document.type.js';
import {
    applyTextFormatToSelection,
    applyFontSizeWuToSelection,
    type SelectionFormatKey,
} from './textbox-formats.js';
import { readSelectionMarkState } from './selection-mark-state.js';
import { readSelectionAlignState } from './selection-align-state.js';
import type { SelectionMarkSnapshot } from './selection-mark-state.type.js';

/**
 * 2026-08-15 (Anke, "clean rewrite") — key for the block-strut decoration
 * plugin (paragraph/heading/listItem sizing, see `maxRunFontSizePx` /
 * `firstRunFontSizePx` below). Its `DecorationSet` depends ONLY on document
 * content, never on the viewport — the edit overlay's zoom is handled by a
 * single CSS `transform: scale(...)` on the content wrapper
 * (`TextboxEditorController#applyContentTransform`), not by re-deriving
 * every mark's pixel size in JS. So, unlike the plugin this replaced, there
 * is no scale-meta dispatch on `viewport-changed` — the decorations only
 * need to recompute when the document itself changes.
 */
const BLOCK_STRUT_PLUGIN_KEY = new PluginKey<DecorationSet>('o2dBlockStrut');

/**
 * The stored px level of the FIRST text run inside `node` that carries a
 * `textStyle.fontSize` mark, or `null` when no run in it is explicitly sized
 * (the node then keeps inheriting the object-level font). Used to size a list
 * item's `::marker` from the text it labels — see `buildFontScaleDecorations`.
 * Thin adapter: `Node.toJSON()` is a standard, cheap ProseMirror API; the
 * actual algorithm lives once, in `textbox-strut-fix.ts`, shared with the
 * view-mode raster's equivalent DOM-walk adapter.
 */
function firstRunFontSizePx(node: ProseMirrorNode): number | null {
    return firstRunFontSizePxFromJson(node.toJSON() as StrutJsonNode);
}

/**
 * The largest stored px level among the text runs and hard breaks (any depth)
 * inside `node` that carry a `textStyle.fontSize` mark, or `null` when none of
 * them are explicitly sized. Fixes Anke's 2026-08-15 bugreport: type text, select
 * all, apply a SMALLER size — line spacing stayed at the old large size.
 * Root cause: a `<p>`/`<h*>` is itself an inline formatting context and
 * generates its own "strut" (a minimum per-line box height) from ITS OWN
 * font-size, wholly independent of whatever smaller size its child runs
 * carry — CSS3 §10.8. Sizing the paragraph itself from its largest marked
 * run — mirroring how `firstRunFontSizePx` sizes a `listItem`'s `::marker`
 * above — kills that mismatched strut. See `maxRunFontSizePxFromJson` in
 * `textbox-strut-fix.ts` for the full rationale; this is a thin adapter.
 */
function maxRunFontSizePx(node: ProseMirrorNode): number | null {
    return maxRunFontSizePxFromJson(node.toJSON() as StrutJsonNode);
}

/**
 * Anke, 2026-07-19 — show the link launcher in the EDIT overlay too.
 *
 * Not for clicking: Anke asked that a click while writing places the caret and
 * never navigates, so the widget is inert (`pointer-events: none`). It is here
 * purely so the text does not REFLOW when entering or leaving edit mode — the
 * view raster reserves space for the icon (`applyLinkPresentation`), and a
 * missing counterpart here would make every line with a URL jump.
 *
 * A widget decoration rather than real content: the icon is presentation, not
 * document. Putting it in the document would let the user select and delete it,
 * and it would travel into `obj.document` and out through every export.
 */
function linkIconDecorations(doc: ProseMirrorNode): Decoration[] {
    const out: Decoration[] = [];
    doc.descendants((node, pos) => {
        if (!node.isText) return;
        const link = node.marks.find((m) => m.type.name === 'link');
        if (!link) return;
        // Track the run's own size, not the paragraph's. The widget sits AFTER
        // the run, so an `em` would resolve against the paragraph and leave a
        // 40px URL with a paragraph-sized arrow (Ankes Fehlerbericht). The
        // stored mark is in world units — no `* scale` needed any more, the
        // ancestor content wrapper's single `transform: scale(...)` already
        // covers every descendant, this icon included.
        const level = parseFontSizePx(
            node.marks.find((m) => m.type.name === 'textStyle')?.attrs.fontSize,
        );
        const iconFontSize =
            level === null ? `${LINK_ICON_SCALE}em` : `${level * LINK_ICON_SCALE}px`;
        // One launcher per RUN end. Adjacent runs carrying the same href would
        // otherwise each get one; TipTap keeps a link as a single text node in
        // practice, and a duplicate icon is a cosmetic issue, not a broken link.
        out.push(
            Decoration.widget(pos + node.nodeSize, () => {
                const el = document.createElement('span');
                el.textContent = LINK_ICON_GLYPH;
                el.setAttribute(LINK_ICON_ATTR, String(link.attrs.href ?? ''));
                el.style.fontSize = iconFontSize;
                el.style.padding = `0 ${LINK_ICON_GAP}`;
                el.style.pointerEvents = 'none';
                return el;
            }, { side: 1 }),
        );
    });
    return out;
}

/**
 * 2026-08-15 (Anke, "clean rewrite") — build the `DecorationSet` for the two
 * remaining, genuinely necessary block-level fixes. Both are pure CSS facts
 * (CSS3 §10.8 — see `maxRunFontSizePx`), unrelated to zoom: raw world-unit
 * `level` values, no `* scale` anywhere. The edit overlay's zoom is handled
 * once, for every descendant at once, by the content wrapper's own CSS
 * `transform: scale(...)` (`#applyContentTransform`) — this plugin no longer
 * needs to know the viewport scale at all.
 */
function buildFontScaleDecorations(doc: ProseMirrorNode, baseFontSizePx: number): DecorationSet {
    // Link launchers ride along in this same set. They depend only on the doc,
    // which this plugin already recomputes on every doc change — a second plugin
    // would duplicate that lifecycle for no gain. See `linkIconDecorations`.
    const decorations: Decoration[] = linkIconDecorations(doc);
    doc.descendants((node, pos) => {
        // O2D.W-PRD-030 — the `::marker` glyph (bullet disc / ordinal number) is
        // drawn by the browser off the `<li>`'s OWN font-size, NOT off the scaled
        // spans nested inside it. Without this the item text follows the per-run
        // mark while the marker stays pinned to the wrapper's object-level font,
        // so an enlarged list shows big text beside a tiny number. Size the
        // listItem from its FIRST sized run (first-run wins on a mixed-size item)
        // so the marker tracks the text it labels. Twin site: `applyListMarkerFontSize`
        // in `textbox-html-builder.ts` does the same for the VIEW raster.
        if (node.type.name === 'listItem') {
            const level = firstRunFontSizePx(node);
            if (level !== null) {
                decorations.push(
                    Decoration.node(pos, pos + node.nodeSize, {
                        style: `font-size: ${level}px`,
                    }),
                );
            }
            return;
        }
        // Bugreport 2026-08-15 (user) — the checklist checkbox is sized in
        // `em` (`CHECKLIST_BOX_SIZE`, textbox-html-builder.ts) specifically so
        // its world-unit width tracks the item's own world-unit font-size
        // (`checkbox width = font-size × factor`), the same way the gap
        // between checkbox and text already does. `em` resolves against the
        // NEAREST ancestor with an explicit font-size — for a `taskItem` that
        // is the `<li>` itself (the checkbox lives in a `<label>` that is a
        // SIBLING of the text `<p>`, not a descendant of it), and nothing
        // was ever setting the `<li>`'s own font-size, so every checkbox sat
        // at the textbox's unrelated BASE size regardless of the item's real
        // (possibly per-run-mixed) size — confirmed live: two items marked
        // 16px and 80px both rendered a 24px-based checkbox (`obj.fontSize`).
        // Sizing the taskItem exactly like listItem (its own largest marked
        // run) fixes the checkbox AND the gap in one place, since both are
        // `em`-relative to this same element. Twin site: `applyBlockFontSizeStrut`
        // in `textbox-html-builder.ts` does the same for the VIEW raster.
        if (node.type.name === 'taskItem') {
            const level = maxRunFontSizePx(node);
            if (level !== null) {
                decorations.push(
                    Decoration.node(pos, pos + node.nodeSize, {
                        style: `font-size: ${level}px`,
                    }),
                );
            }
            return;
        }
        // Bugreport 2026-08-15 (Anke) — see `maxRunFontSizePx`: a `<p>`/`<h*>`
        // generates its own line-box strut from ITS OWN font-size, independent
        // of any smaller size its child runs carry. Sizing the paragraph from
        // its largest marked run kills that mismatch in both directions.
        // `paragraph`/`heading` cover every block that renders inline runs
        // directly, including one nested inside a `listItem`/`taskItem` (both
        // wrap a `paragraph` child by the schema's default content model), so
        // list items get the same fix without a second special case.
        if (node.type.name === 'paragraph' || node.type.name === 'heading') {
            const level = maxRunFontSizePx(node);
            if (level !== null) {
                decorations.push(
                    Decoration.node(pos, pos + node.nodeSize, {
                        style: `font-size: ${level}px`,
                    }),
                );
                // Bugreport 2026-08-16 (follow-up), twin fix to
                // `protectUnmarkedRunsFromCascade` in `textbox-html-builder.ts`:
                // the node decoration above sets the PARAGRAPH's own font-size,
                // which any UNMARKED run inside it (no `textStyle.fontSize` mark
                // of its own) would otherwise silently inherit via the CSS
                // cascade instead of staying at the textbox's base size — e.g.
                // selecting and enlarging only PART of a paragraph must not
                // visibly grow the rest of it. Pin every unmarked run to its own
                // explicit `baseFontSizePx` so it stops participating in that
                // cascade. No such fix is needed for the `taskItem`/`listItem`
                // branches above: their text always lives in a NESTED
                // `paragraph` child, which this same branch already corrects at
                // its own position when the walk reaches it.
                decorations.push(...unmarkedRunDecorations(node, pos, baseFontSizePx));
            }
        }
    });
    return DecorationSet.create(doc, decorations);
}

function unmarkedRunDecorations(
    node: ProseMirrorNode,
    pos: number,
    baseFontSizePx: number,
): Decoration[] {
    const out: Decoration[] = [];
    let offset = 0;
    node.forEach((child) => {
        if (child.isText) {
            const hasOwnSize = child.marks.some(
                (m) => m.type.name === 'textStyle' && parseFontSizePx(m.attrs.fontSize) !== null,
            );
            // Twin fix to `protectUnmarkedRunsFromCascade` (textbox-html-builder.ts):
            // skip pure-whitespace runs, same as its DOM counterpart. Unlike the
            // read-only view raster, this decoration lives inside the LIVE
            // contenteditable ProseMirror view — an extra inline decoration
            // boundary around a bare whitespace run (e.g. a lone space at a mark
            // boundary from a partial selection) would needlessly fragment the
            // text-node structure ProseMirror relies on for cursor navigation,
            // IME composition, and selection mapping.
            if (!hasOwnSize && child.text?.trim() !== '') {
                const from = pos + 1 + offset;
                out.push(
                    Decoration.inline(from, from + child.nodeSize, {
                        style: `font-size: ${baseFontSizePx}px`,
                    }),
                );
            }
        }
        offset += child.nodeSize;
    });
    return out;
}

/**
 * 2026-08-15 (Anke, "clean rewrite") — the ProseMirror plugin that applies
 * the block-strut decorations above. Recomputes only when the document
 * actually changes (standard ProseMirror decoration-plugin shape) — there is
 * no scale-meta dispatch any more, since the decorations no longer depend on
 * the viewport.
 */
function createBlockStrutPlugin(baseFontSizePx: number): Plugin<DecorationSet> {
    return new Plugin<DecorationSet>({
        key: BLOCK_STRUT_PLUGIN_KEY,
        state: {
            init: (_config, editorState) => buildFontScaleDecorations(editorState.doc, baseFontSizePx),
            apply: (tr, previous, _oldState, newState) =>
                tr.docChanged ? buildFontScaleDecorations(newState.doc, baseFontSizePx) : previous,
        },
        props: {
            decorations(state) {
                return BLOCK_STRUT_PLUGIN_KEY.getState(state) ?? DecorationSet.empty;
            },
        },
    });
}

/**
 * Optional bridge into the host element for visual side-effects: hiding the
 * underlying Konva content shape while editing, triggering a content-layer
 * redraw, and resolving a screen-relative bbox to position the temporary
 * `<div contentEditable>` overlay (TR4 Variante A).
 */
export interface TextboxEditorBindings {
    konvaNodeFor(id: ObjectId): Konva.Node | undefined;
    redrawContent(): void;
    screenBboxFor(id: string):
        | { x: number; y: number; width: number; height: number; rotation: number }
        | null;
}

/**
 * Lit ReactiveController that owns the textbox edit-mode DOM surface (TR4).
 *
 * Variant A: a temporary `<div contentEditable>` is created on
 * `edit/enter-textbox`, positioned over the Konva textbox via
 * `host.screenBboxFor(id)`, and torn down on commit/cancel. Tab key
 * commits, Escape cancels. Click-outside commit and format application
 * (`textbox/apply-format`, `textbox/apply-fontsize`) land in T13.
 *
 * Visibility-Sequenz (TR4 MINOR-11): the editor DOM is created with
 * `visibility:'hidden'`, positioned, the underlying Konva content shape is
 * hidden, then the editor flips to `visibility:'visible'` — so there is
 * never a frame where both are visible.
 *
 * FC10: enter for unknown / non-textbox ids is rejected silently.
 * FC11: bus subscription errors are swallowed (DEV warning only).
 */
export class TextboxEditorController implements ReactiveController {
    readonly #host: ReactiveControllerHost & HTMLElement;
    readonly #bus: WhiteboardBus;
    readonly #state: BoardState;
    readonly #history: History<BoardState>;
    readonly #bindings: TextboxEditorBindings | null;
    readonly #disconnect$ = new Subject<void>();

    #editor: HTMLDivElement | null = null;
    // 2026-08-15 (Anke, "clean rewrite") — the UNSCALED inner content wrapper
    // TipTap actually mounts into (see `buildTextboxEditContentElement`).
    // `#editor` (outer) stays screen-px positioned/rotated as before; this
    // element carries the object's own world-unit sizing and gets the single
    // `transform: scale(...)` that replaces the old per-node decoration math.
    #editorContent: HTMLDivElement | null = null;
    #tiptap: Editor | null = null;
    #editingId: ObjectId | null = null;
    #readonly = false;
    #onDocMouseDown: ((e: MouseEvent) => void) | null = null;

    // O2D.W-PRD-026 / TR1+TR4: unsubscribe handle for the per-edit
    // `viewport-changed` subscription. Set in `#openEditor`, called + cleared
    // in `#removeEditorDom` (the single teardown convergence point) so the
    // subscription never leaks past commit/cancel/hostDisconnected.
    #viewportUnsub: (() => void) | null = null;

    // O2D.W-PRD-032 / TR2 (DEFECT A): unsubscribe handle for the per-edit
    // `object-updated` subscription that re-styles the live overlay alignment
    // (h + v) while editing. Set in `#openEditor`, called + cleared in
    // `#removeEditorDom` next to `#viewportUnsub` so it never leaks past
    // commit/cancel/hostDisconnected and never double-subscribes on re-open.
    #objectUpdatedUnsub: (() => void) | null = null;

    // O2D.W-PRD-027 / TR2: stable handler ref for the live selection-state
    // listener registered on the TipTap editor (`selectionUpdate` + `update`).
    // `editor.on()` returns the editor (chainable), NOT an unsubscribe fn — so
    // the SAME ref must be passed to `editor.off(event, handler)` in
    // `#removeEditorDom` to detach. Set in `#openEditor`, detached + cleared in
    // `#removeEditorDom` (the single teardown convergence point) so the listener
    // never leaks past edit-exit and never double-subscribes on re-open (FC4).
    #onSelectionChange: (() => void) | null = null;

    // PRD-RTE-TIPTAP-FIX §RC-C / Task T3.2 — cached TipTap selection captured
    // on the capturing-phase mousedown over an optionsbar element. The browser
    // migrates focus to the clicked button on `mousedown`, which collapses
    // `EditorState.selection` before `applyFormat` runs; caching the
    // `from/to` indices here lets us restore the user-intended range via
    // `chain.setTextSelection(...)` before the mark command. Both fields are
    // cleared after each `applyFormat` call (and at every edit-exit path,
    // wired in T3.3) so a stale range cannot leak into the next edit session.
    #cachedSelectionFrom: number | null = null;
    #cachedSelectionTo: number | null = null;

    constructor(
        host: ReactiveControllerHost & HTMLElement,
        bus: WhiteboardBus,
        state: BoardState,
        history: History<BoardState>,
        bindings: TextboxEditorBindings | null = null,
    ) {
        this.#host = host;
        this.#bus = bus;
        this.#state = state;
        this.#history = history;
        this.#bindings = bindings;
    }

    hostConnected(): void {
        this.#bus.uiActions$
            .pipe(takeUntil(this.#disconnect$), takeUntil(this.#bus.destroy$))
            .subscribe({
                next: (action) => this.#handle(action),
                error: () => {
                    if (import.meta.env?.DEV) {
                        console.warn('[TextboxEditorController] bus error swallowed');
                    }
                },
            });

        // Capture-phase mousedown so the editor commits BEFORE Konva
        // processes the click and clears the selection. Whitelist the
        // optionsbar so format buttons do not trigger commit.
        this.#onDocMouseDown = (e: MouseEvent): void => {
            if (!this.#editor || !this.#editingId) return;
            const target = e.target as Node | null;
            if (this.#editor.contains(target)) return;
            if (target instanceof Element && target.closest('o2d-svg-optionsbar')) {
                // PRD-RTE-TIPTAP-FIX §RC-C / T3.2: capture the live TipTap
                // selection BEFORE the browser migrates focus to the clicked
                // optionsbar button. The capturing-phase mousedown is
                // synchronous and fires before focus transfer, so the
                // selection is still the user-intended range at this point.
                this.#cacheCurrentSelection();
                return;
            }
            this.#dispatchCommit();
        };
        document.addEventListener('mousedown', this.#onDocMouseDown, true);
    }

    hostDisconnected(): void {
        if (this.#onDocMouseDown) {
            document.removeEventListener('mousedown', this.#onDocMouseDown, true);
            this.#onDocMouseDown = null;
        }
        this.#disconnect$.next();
        this.#disconnect$.complete();
        this.#removeEditorDom();
        this.#editingId = null;
    }

    /**
     * Delegate selection formatting (bold, color, etc.) into the active
     * editor. Called by `TextboxLifecycleController` on
     * `textbox/apply-format` UiAction (T15). No-op when no editor is open.
     */
    applyFormat(formatKey: SelectionFormatKey, value: string | number): boolean {
        if (!this.#editor) return false;
        // PRD-RTE-TIPTAP §TR7: when a TipTap editor is mounted, route
        // toggle/color/fontSize formats through its `chain()` API. The legacy
        // raw-DOM fallback (`applyTextFormatToSelection`) only runs for keys
        // that have no TipTap-mark equivalent or when no editor is open
        // (e.g. sticky-bridge). As of PRD-RTE-TIPTAP-MARKS all currently
        // wired selection-format keys are handled by the switch below.
        if (this.#tiptap) {
            const chain = this.#tiptap.chain().focus();
            // PRD-RTE-TIPTAP-FIX §RC-C / T3.3: restore the cached selection
            // (captured on capturing-phase mousedown over an optionsbar
            // element) before applying any inline mark, so the mark targets
            // the user-selected range rather than the collapsed cursor that
            // browser focus migration left behind. Applied uniformly to all
            // six inline-mark keys; the cache is cleared after each call so
            // a stale range cannot leak into the next session (Failure
            // Conditions §"stale cache leak").
            this.#restoreCachedSelection(chain);
            switch (formatKey) {
                case 'bold':
                    chain.toggleBold().run();
                    this.#clearCachedSelection();
                    return true;
                case 'italic':
                    chain.toggleItalic().run();
                    this.#clearCachedSelection();
                    return true;
                case 'strike':
                    chain.toggleStrike().run();
                    this.#clearCachedSelection();
                    return true;
                case 'underline':
                    chain.toggleUnderline().run();
                    this.#clearCachedSelection();
                    return true;
                case 'color':
                    chain.setMark('textStyle', { color: String(value) }).run();
                    this.#clearCachedSelection();
                    return true;
                case 'fontSize': {
                    // O2D.W-PRD-030 §TR3: single convention — the stored mark is
                    // written verbatim in cluster-world-units (bare level px, NO
                    // `× scale`), consistent with `applyFontSize`. Level buttons
                    // route through `applyFontSize`, so this path is effectively
                    // dead for them; kept convention-consistent for any direct
                    // `applyFormat('fontSize')` caller.
                    const px = typeof value === 'number' ? `${value}px` : String(value);
                    chain.setMark('textStyle', { fontSize: px }).run();
                    this.#clearCachedSelection();
                    return true;
                }
                case 'fontWeight':
                    // PRD-RTE-TIPTAP-MARKS §TR2: route font-weight through the
                    // `fontWeight` textStyle attribute so it lands as a per-run
                    // mark instead of a whole-object property change.
                    chain.setMark('textStyle', { fontWeight: String(value) }).run();
                    this.#clearCachedSelection();
                    return true;
                case 'fontStyle':
                    // PRD-RTE-TIPTAP-MARKS §DD1: the optionsbar only offers
                    // `italic` for the `font-style` intent — collapse to the
                    // existing StarterKit italic mark.
                    if (value === 'italic') {
                        chain.toggleItalic().run();
                    }
                    this.#clearCachedSelection();
                    return true;
                case 'textDecoration':
                    // PRD-RTE-TIPTAP-MARKS §DD2: value-dispatch to the existing
                    // underline / strike marks from PRD-RTE-TIPTAP-FIX.
                    if (value === 'underline') {
                        chain.toggleUnderline().run();
                    } else if (value === 'line-through') {
                        chain.toggleStrike().run();
                    }
                    this.#clearCachedSelection();
                    return true;
                case 'textTransform':
                    // Anke, 2026-07-22 — ging früher über ein blindes `setMark`
                    // und ließ sich darum nicht wieder ausschalten. Jetzt über
                    // denselben Set-vs-Normalize-Weg wie verticalScript: erneutes
                    // Klicken des aktiven Werts löscht ihn (nur im markierten
                    // Bereich).
                    return this.#applyValuedTextStyle(chain, 'textTransform', String(value));
                case 'verticalScript':
                    // O2D.W-PRD-033 §TR1/TR2/TR3: the optionsbar always
                    // dispatches a SET; the controller inspects its own
                    // selection snapshot and toggles the active value back to
                    // normal (per-run on a mixed selection) instead of
                    // re-setting it. The optionsbar dispatch is unchanged.
                    return this.#applyValuedTextStyle(chain, 'verticalScript', String(value));
                default:
                    // Fall through to the raw-DOM fallback for keys without a
                    // direct TipTap-mark mapping.
                    break;
            }
        }
        return applyTextFormatToSelection(formatKey, value);
    }

    /**
     * Apply a world-unit font-size to the current selection inside the
     * active editor. Called by `TextboxLifecycleController` on
     * `textbox/apply-fontsize` UiAction (T15). No-op when no editor is open.
     */
    applyFontSize(wu: number, scale: number): boolean {
        if (!this.#editor) return false;
        // PRD-RTE-TIPTAP §TR7 / §T5.4: when a TipTap editor is mounted, route
        // the world-unit font-size through `chain().setMark('textStyle', ...)`
        // so it lands as a proper ProseMirror mark in `obj.document` instead of
        // a raw-DOM `<span data-font-wu>` wrapper. The whole-object fallback
        // (`UpdatePropertyCommand(id, 'fontSize', wu)`) is dispatched
        // unconditionally by the call site (see
        // `o2d-whiteboard.element.ts#setTextSizeForSelected`) so the obj-level
        // size still updates after commit even when no selection exists.
        if (this.#tiptap) {
            // O2D.W-PRD-030 §TR2/TR3: the stored `textStyle.fontSize` mark uses a
            // SINGLE convention — cluster-world-units (`${wu}px`), NOT screen
            // pixels. Display scaling to `level × scale` happens at render/edit
            // time (SVG raster + the edit-overlay Decoration.inline plugin, TR4),
            // never in the persisted mark. Writing `${wu * scale}px` here would
            // re-introduce the two-convention mismatch that produced false-MIXED
            // selection state across zoom levels.
            const px = `${wu}px`;
            this.#tiptap.chain().focus().setMark('textStyle', { fontSize: px }).run();
            return true;
        }
        return applyFontSizeWuToSelection(wu, scale);
    }

    /**
     * O2D.W-PRD-035 §TR1 — apply a list type to the current selection inside
     * the active editor by driving the live TipTap `toggle*List` commands.
     * Called by `TextboxLifecycleController` on the `textbox/apply-list`
     * UiAction. No-op (returns `false`) when no editor is open.
     *
     * The commands are selection/block-scoped: `toggleBulletList` /
     * `toggleOrderedList` / `toggleTaskList` derive their block range from the
     * current selection, so only the paragraph(s) under the caret change
     * (per-paragraph / mixed docs fall out natively). They are TOGGLES —
     * calling the currently-active kind again lifts the list back to plain
     * paragraphs. The cached optionsbar selection is restored first (mirroring
     * `applyFormat`) so the toggle targets the user-intended range rather than
     * the collapsed caret left behind by focus migration on the button click.
     */
    applyListType(target: 'bullet' | 'ordered' | 'checklist'): boolean {
        if (!this.#editor || !this.#tiptap) return false;
        const chain = this.#tiptap.chain().focus();
        this.#restoreCachedSelection(chain);
        switch (target) {
            case 'bullet':
                chain.toggleBulletList().run();
                break;
            case 'ordered':
                chain.toggleOrderedList().run();
                break;
            case 'checklist':
                chain.toggleTaskList().run();
                break;
        }
        this.#clearCachedSelection();
        return true;
    }

    /**
     * Apply a horizontal alignment to the block(s) under the caret inside the
     * active editor via TipTap's `setTextAlign` (from the TextAlign extension).
     * Called by `TextboxLifecycleController` on the `textbox/apply-align`
     * UiAction. No-op (returns `false`) when no editor is open.
     *
     * Block-scoped like `applyListType`: `setTextAlign` writes the `text-align`
     * attribute onto every paragraph/heading in the current selection, so only
     * the block(s) under the caret change (per-paragraph / mixed docs fall out
     * natively — Word-like). The cached optionsbar selection is restored first
     * (mirroring `applyFormat` / `applyListType`) so the change targets the
     * user-intended range rather than the collapsed caret left behind by focus
     * migration on the button click.
     */
    applyTextAlign(align: 'left' | 'center' | 'right'): boolean {
        if (!this.#editor || !this.#tiptap) return false;
        const chain = this.#tiptap.chain().focus();
        this.#restoreCachedSelection(chain);
        chain.setTextAlign(align).run();
        this.#clearCachedSelection();
        return true;
    }

    /**
     * Commit the active textbox edit (if any). No-op when no editor is open.
     *
     * Used by `O2dWhiteboardElement.config` setter (PRD-G T08) to flush any
     * pending in-flight textbox edit before the board state is replaced via
     * `BoardState.replaceAll()`. Internally dispatches the `edit/commit`
     * UiAction so the existing commit pipeline (UpdatePropertyCommand →
     * History) runs unchanged.
     */
    commitActive(): void {
        if (this.#editor === null || this.#editingId === null) return;
        this.#dispatchCommit();
    }

    /**
     * Whether the active editor currently has a NON-empty (range) text
     * selection — i.e. the user has highlighted part of the text.
     *
     * The host element uses this to decide the scope of a font-size change
     * (size-scope fix): a real range selection means "apply to the highlighted
     * run only" (via the TipTap mark in {@link applyFontSize}), so the
     * whole-object `fontSize` MUST NOT be overwritten. A collapsed caret (or no
     * editor at all) means "apply to the whole box" — treated the same as
     * nothing selected — so the object-level property update is allowed.
     *
     * Returns `false` when no editor is mounted or the selection is empty.
     */
    hasRangeSelection(): boolean {
        if (!this.#tiptap) return false;
        return !this.#tiptap.state.selection.empty;
    }

    /**
     * O2D.W-PRD-027 §TR1 — read the live selection's formatting state as a
     * 3-state (ON/OFF/MIXED) snapshot, delegating to the pure
     * `readSelectionMarkState` reader. PURE READ: it only inspects
     * `EditorState` (`state.doc` / `state.selection` / `storedMarks`) and never
     * mutates the document or steals focus (Failure Condition FC1 / Goal G4).
     *
     * Returns `null` when no editor is mounted, so the live-wiring handler can
     * skip emitting an `edit/selection-changed` action outside an edit session.
     */
    getSelectionMarkState(): SelectionMarkSnapshot | null {
        if (!this.#tiptap) return null;
        return readSelectionMarkState(this.#tiptap.state);
    }

    /**
     * O2D.W-PRD-035 §TR2 — the id of the textbox whose rich-text editor is
     * currently open, or `null` when no edit session is active. Read by the
     * board's `isTextboxEditing` accessor so the optionsbar can route a list
     * click to the live editor (editing) vs. the whole-textbox command (not
     * editing).
     */
    get editingId(): ObjectId | null {
        return this.#editingId;
    }

    /**
     * TR4 readonly defense-in-depth: when readonly, `openEditor` is a no-op.
     * If invoked while editing, the open editor is closed without committing.
     */
    setReadonly(value: boolean): void {
        this.#readonly = value;
        if (value && this.#editingId !== null) {
            this.#closeEditor();
        }
    }

    /**
     * O2D.W-PRD-035 §R2 — derive the current selection's list block type from
     * the live editor (`isActive('bulletList'/'orderedList'/'taskList')`), or
     * `null` when the caret sits in a plain paragraph. Selection-scoped: it
     * reports the block at the caret, so a mixed document lights up exactly the
     * button matching the paragraph under the caret.
     */
    #readSelectionListBlock(tiptap: Editor): 'bullet' | 'ordered' | 'checklist' | null {
        if (tiptap.isActive('bulletList')) return 'bullet';
        if (tiptap.isActive('orderedList')) return 'ordered';
        if (tiptap.isActive('taskList')) return 'checklist';
        return null;
    }

    #handle(action: UiAction): void {
        switch (action.type) {
            case 'edit/enter-textbox':
                this.#openEditor(action.id, action.at);
                return;
            case 'edit/commit':
                this.#commit(action.id, action.text, action.document);
                return;
            case 'edit/cancel':
                this.#cancel(action.id);
                return;
            default:
                return;
        }
    }

    #openEditor(id: ObjectId, at?: { readonly clientX: number; readonly clientY: number }): void {
        if (this.#readonly) return;
        const obj = this.#state.getObject(id);
        // FC10: silently reject unknown / non-textbox ids.
        if (!obj || !isTextBox(obj)) return;

        // PRD-RTE-TIPTAP §FC1: guard against dual-editor leak — destroying any
        // existing editor (and its TipTap instance) before constructing a new
        // one. `#removeEditorDom` calls `#tiptap.destroy()` first.
        this.#removeEditorDom();

        // Outer wrapper: screen-px position/size/rotation only (unchanged from
        // before — `#positionEditor` still computes this in already-scaled
        // screen pixels via `screenBboxFor`). No font/padding/vAlign styling
        // lives here any more — that all moved to the inner content wrapper.
        const editor = document.createElement('div');
        editor.classList.add('textbox-editor');
        editor.style.position = 'absolute';
        // TR4 MINOR-11: start hidden, position, hide Konva, then reveal.
        editor.style.visibility = 'hidden';
        editor.style.pointerEvents = 'auto';
        // No own outline/border: the Konva `.textbox-bg` rect (with the
        // object's own border) stays visible underneath the transparent
        // editor overlay. An editor-side outline would visually replace the
        // object border and break edit/view parity.
        editor.style.caretColor = 'currentColor';
        editor.style.cursor = 'text';
        editor.style.overflow = 'hidden';

        this.#positionEditor(editor, id);

        // Inner wrapper: the object's OWN world-unit width/height/font-size/
        // padding (see `buildTextboxEditContentElement`) — i.e. exactly what
        // the persisted document's marks already assume. TipTap mounts
        // directly into this element and populates its DOM; there is no
        // static `generateHTML` content to clear first (unlike the old
        // `buildTextboxHtmlElement`-based wrapper, this one starts empty).
        const content = buildTextboxEditContentElement(obj);
        content.classList.add('textbox-editor-content');
        content.spellcheck = false;
        editor.appendChild(content);
        this.#editorContent = content;

        // A single CSS transform does what the old per-node decoration math
        // (`#applyScaledFont` + the font-scale ProseMirror plugin) used to
        // reconstruct by hand: scale EVERY descendant — font-sizes, padding,
        // line-heights, icons — at once, natively, via the browser's own
        // cascade. Applied once here (edit-enter-while-zoomed) and again on
        // every `viewport-changed` tick below.
        this.#applyContentTransform(content);

        this.#editor = editor;
        this.#editingId = id;

        const container =
            this.#host.querySelector<HTMLElement>('.board-root') ?? this.#host;
        container.appendChild(editor);

        // O2D.W-PRD-026 / TR1+TR2: the edit overlay is a deterministic
        // projection of (textbox world rect × viewport). Track every
        // `viewport-changed` (zoom AND pan) for the duration of the edit:
        // re-run `#positionEditor` (screen rect, outer wrapper) and re-apply
        // the single content-scale transform (inner wrapper). Neither touches
        // the ProseMirror document — no `applyFontSize`/`setMark` here, which
        // would steal focus per wheel-tick. The handler early-returns when no
        // editor is active so a stray late event after teardown is a no-op.
        this.#viewportUnsub = this.#state.on('viewport-changed', () => {
            const activeEditor = this.#editor;
            const activeContent = this.#editorContent;
            const activeId = this.#editingId;
            if (!activeEditor || !activeContent || activeId == null) return;
            this.#positionEditor(activeEditor, activeId);
            this.#applyContentTransform(activeContent);
        });

        // PRD-RTE-TIPTAP §TR5: mount a TipTap Editor into the inner content
        // wrapper. The canonical content source is `obj.document` (TipTap
        // JSON doc); the legacy `obj.text` field is ignored here because the
        // slice hydrator (T2.5) attaches `document` on load via
        // `textToDocument`.
        this.#tiptap = new Editor({
            element: content,
            extensions: EDIT_TEXTBOX_EXTENSIONS,
            // Cast: TextDocument is structurally JSONContent but TipTap's option
            // type is mutable; the editor only reads from `content` so the
            // readonly source is safe.
            content: (obj.document as unknown) as Record<string, unknown>,
            // Anke, 2026-07-18 — text pasted from OUTSIDE adopts the formatting
            // of the character before the caret; text copied from a textbox on
            // this board keeps its own. Rule and caveat: `paste-format.ts`.
            editorProps: { handlePaste: handleTextboxPaste },
        });

        // 2026-08-15 (Anke, "clean rewrite") — register the block-strut
        // decoration plugin (paragraph/heading/listItem sizing). Unlike the
        // font-scale plugin this replaced, its decorations depend only on
        // document content, not on the viewport, so there is nothing to seed
        // with the current scale and nothing to re-dispatch on zoom.
        this.#tiptap.registerPlugin(createBlockStrutPlugin(obj.fontSize));

        // PRD-RTE-TIPTAP §T4.2 step 5: seed default marks so newly typed text
        // inherits the object-level color + font-size without the user needing
        // to re-apply them via the optionsbar. Attributes are bound to locals
        // so the meta-test (style-source-of-truth) does not flag a CSS color
        // assignment here — this is a TipTap mark attribute, not CSS.
        const seedColor = obj.color;
        const seedFontSize = `${obj.fontSize}px`;
        try {
            this.#tiptap
                .chain()
                .focus()
                .setMark('textStyle', { color: seedColor, fontSize: seedFontSize })
                .run();
        } catch {
            // Defensive: if no `textStyle` mark is registered (mis-config) or
            // the editor view is not yet ready, skip default-mark seeding —
            // the user can still type with the obj-level defaults applied via
            // wrapper CSS.
        }

        // O2D.W-PRD-027 §TR2: register the live selection-state listener so the
        // optionsbar reflects the highlighted run's actual formatting on every
        // caret move / selection change / content edit while the session is
        // open. Build a STABLE handler ref (used for both `on` and the matching
        // `off` in `#removeEditorDom`) — `editor.on()` returns the editor, not
        // an unsubscribe fn, so an explicit `off(event, sameRef)` is the only
        // correct teardown (Failure Condition FC4). The handler is a pure read
        // (`readSelectionMarkState`) — it never mutates the doc or steals focus.
        const emitSelectionState = (): void => {
            const tiptap = this.#tiptap;
            const activeId = this.#editingId;
            if (!tiptap || activeId == null) return;
            // Anke, 2026-07-16: an unaligned block inherits the textbox-wide
            // default, so the reader needs it to report what the caret's
            // paragraph actually renders as. Re-read per emit rather than
            // captured once — `hAlign` can change under an open session (the
            // `object-updated` subscription below reacts to exactly that).
            const current = this.#state.getObject(objectId(activeId));
            const boxAlign = current && isTextBox(current) ? current.hAlign : undefined;
            this.#bus.uiActions$.next({
                type: 'edit/selection-changed',
                id: objectId(activeId),
                marks: readSelectionMarkState(tiptap.state),
                // O2D.W-PRD-035 §R2: carry the current selection's block type so
                // the optionsbar list-button active-state is selection-scoped
                // while editing. Re-emitted on every selectionUpdate/update, so
                // moving the caret across a mixed doc tracks the block at the
                // new caret position.
                listBlock: this.#readSelectionListBlock(tiptap),
                // Anke, 2026-07-16: same idea for the alignment — the align
                // buttons already WROTE per paragraph, so they must READ per
                // paragraph too, or the circle shows the box default while the
                // caret sits in a differently aligned paragraph.
                alignBlock: readSelectionAlignState(tiptap.state, boxAlign),
            });
        };
        this.#onSelectionChange = emitSelectionState;
        this.#tiptap.on('selectionUpdate', emitSelectionState);
        this.#tiptap.on('update', emitSelectionState);
        // Reflect the initial (seeded, collapsed-caret) state immediately so the
        // optionsbar highlights the current formatting without waiting for the
        // first user-driven selection change.
        emitSelectionState();

        // Hide Konva content shape so the user sees only the editor overlay
        // (prevents double-text ghosting).
        const node = this.#bindings?.konvaNodeFor(id);
        if (node instanceof Konva.Group) {
            node.findOne<Konva.Shape>('.textbox-content')?.visible(false);
            // PRD-RTE-OVERFLOW §TR3 / T2.4: explicitly retract the overset "+"
            // indicator on edit enter. The renderer's `__editingActive` guard
            // only suppresses FUTURE raster-driven `visible(...)` flips; it does
            // not hide an indicator that is already visible from the last
            // view-mode raster. Hide it here so the edit overlay is never
            // cluttered by the indicator.
            node.findOne<Konva.Node>('.textbox-overflow-indicator')?.visible(false);
            // Mark group as editing-active so the raster subscriber suppresses
            // `content.visible(true)` flips while the editor overlay is open.
            // Prevents bitmap ghosting on in-edit style mutations (PRD-RTE-PARITY-FIX G2).
            (node as TextBoxGroup).__editingActive = true;
            this.#bindings?.redrawContent();
        }

        editor.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                this.#dispatchCancel();
                return;
            }
            if (e.key === 'Tab') {
                e.preventDefault();
                this.#dispatchCommit();
                return;
            }
        });

        // O2D.W-PRD-032 §TR2(d) — seed the overlay alignment at edit-enter.
        // `buildTextboxEditContentElement` already set an initial `vAlign`
        // `justify-content` on `content`, but `#applyAlignStyles` re-applies
        // both axes anyway — `vAlign` can change mid-edit-session (written
        // unconditionally by the optionsbar, unlike `hAlign` which is gated
        // behind `!isTextboxEditing`), which is exactly why the
        // `object-updated` subscription below exists.
        this.#applyAlignStyles(content, obj);

        // O2D.W-PRD-032 §TR2 (DEFECT A) — track every `object-updated` for the
        // edited textbox so an align change from the optionsbar re-styles the
        // LIVE overlay. Mirrors ONLY the `viewport-changed` subscription+teardown
        // lifecycle — NOT its handler body: this is CSS-only (`#applyAlignStyles`),
        // never `#positionEditor`/`#applyContentTransform`, so it dispatches no
        // ProseMirror transaction and never steals the caret. The handler
        // early-returns when no editor is active (stray late event after
        // teardown) and filters to the edited id + textbox guard.
        this.#objectUpdatedUnsub = this.#state.on('object-updated', (e) => {
            const activeContent = this.#editorContent;
            const activeId = this.#editingId;
            if (!activeContent || activeId == null) return;
            if (e.object.id !== activeId || !isTextBox(e.object)) return;
            this.#applyAlignStyles(activeContent, e.object);
        });

        // Visibility-Sequenz step 4: editor visible (Konva already hidden).
        editor.style.visibility = 'visible';

        // Anke, 2026-09-12: "der Cursor [soll] nicht an den Textanfang
        // springen, sondern an der Stelle landen, wo der Cursor klickt" — `at`
        // carries the native click's viewport coordinates (set only for a
        // click-driven edit-enter; a brand-new textbox has none). Placed
        // AFTER the mark-seeding `.focus()` chain above so this is the caret
        // position that actually sticks, and after the visibility flip only
        // for tidiness — `visibility: hidden` (unlike `display: none`) keeps
        // layout intact, so `posAtCoords` would resolve correctly even
        // before it. Falls back to whatever `.focus()` already set (start of
        // document) when the point doesn't resolve to one, e.g. a stale
        // coordinate from before the overlay finished laying out.
        if (at) {
            const pos = this.#tiptap.view.posAtCoords({ left: at.clientX, top: at.clientY });
            if (pos) {
                this.#tiptap.chain().focus().setTextSelection(pos.pos).run();
            }
        }
    }

    // O2D.W-PRD-032 §TR2 (DEFECT A) — apply the object's alignment to the LIVE
    // edit overlay's content wrapper CSS-only: `justify-content` from
    // `vAlign` (can change mid-edit-session, written unconditionally by the
    // optionsbar — see the `object-updated` subscription above) and
    // `text-align` from `hAlign`, onto `content` + `.ProseMirror`, matching
    // the view-mode wrapper (`justify-content` on the outer div, `text-align`
    // on the inner span — deliberate non-parity in WHERE it lives, same
    // values). No document mutation, no re-focus — the caret is untouched.
    #applyAlignStyles(content: HTMLDivElement, obj: TextBox): void {
        content.style.justifyContent = VALIGN_LAYOUT[obj.vAlign]?.justifyContent ?? 'flex-start';
        content.style.textAlign = obj.hAlign;
        const pm = content.querySelector<HTMLElement>('.ProseMirror');
        if (pm) pm.style.textAlign = obj.hAlign;
    }

    #commit(id: ObjectId, text: string, document?: TextDocument): void {
        const target = this.#state.getObject(id);
        if (!target || !isTextBox(target)) {
            this.#closeEditor();
            return;
        }
        // Prefer the canonical TipTap document when present (PRD-RTE-TIPTAP §TR5).
        // Falling back to `text` keeps non-TipTap callers (legacy tests, sticky
        // bridge) working until the migration cycle completes.
        if (document !== undefined) {
            // O2D.W-PRD-035 §TR3: the committed `document` is authoritative for
            // list structure; `obj.listType` is an advisory hint DERIVED from it
            // (fed the committed `document` param, NOT `#tiptap.getJSON()`).
            // Write both ATOMICALLY as one undo step via `SetListTypeCommand`.
            // `SetListTypeCommand` has no change-guard, so extend the `#commit`
            // guard to also compare `listType`: push only when the document OR
            // the derived listType actually changed (else an unchanged commit
            // adds a spurious extra Ctrl+Z step).
            const derivedListType = deriveAdvisoryListType(document);
            const prev = JSON.stringify(target.document ?? null);
            const next = JSON.stringify(document);
            if (prev !== next || target.listType !== derivedListType) {
                const cmd = new SetListTypeCommand(id, derivedListType, document);
                this.#history.push(cmd, this.#state);
            }
        } else if (target.text !== text) {
            const cmd = new UpdatePropertyCommand(id, 'text', text);
            this.#history.push(cmd, this.#state);
        }
        this.#closeEditor();
    }

    #cancel(_id: ObjectId): void {
        this.#closeEditor();
    }

    #dispatchCommit(): void {
        const id = this.#editingId;
        const content = this.#editorContent;
        if (!id || !content) return;
        // PRD-RTE-TIPTAP §TR5: emit the TipTap JSON doc as canonical payload
        // when an editor is mounted. `text` stays populated for back-compat
        // with the non-TipTap commit path (sticky bridge, legacy tests).
        // Anke, 2026-07-19 — mark any address the live autolinker never reached.
        // It fires on a word boundary, so a document ENDING on the URL never got
        // the mark and therefore never got its launcher icon. See
        // `autolink-document.ts`.
        const document = this.#tiptap
            ? autolinkDocument(this.#tiptap.getJSON() as unknown as TextDocument)
            : undefined;
        // O2D.W-PRD-035 §D1: the committed `document` is the sole source of
        // truth for list structure. The lossy `documentToListText`-derived
        // legacy list `text` write is REMOVED (it collapsed a mixed doc to a
        // single kind). `text` now always carries the overlay innerHTML for the
        // non-TipTap back-compat path; `obj.text` is no longer maintained for
        // lists. The physical `text` field retirement is O2D.W-PRD-036.
        // Reads off `content` (the inner wrapper TipTap actually mounts into),
        // NOT the outer `#editor` — the outer only carries positioning, no
        // longer any content.
        const text = content.innerHTML;
        this.#bus.uiActions$.next({
            type: 'edit/commit',
            id: objectId(id),
            text,
            document,
        });
    }

    #dispatchCancel(): void {
        const id = this.#editingId;
        if (!id) return;
        this.#bus.uiActions$.next({ type: 'edit/cancel', id: objectId(id) });
    }

    #closeEditor(): void {
        const id = this.#editingId;
        if (this.#editor) {
            this.#editor.style.display = 'none';
            this.#editor.style.pointerEvents = 'none';
        }
        this.#removeEditorDom();
        this.#editingId = null;

        if (id == null) return;
        const node = this.#bindings?.konvaNodeFor(id);
        if (node instanceof Konva.Group) {
            // Clear the editing-active flag BEFORE re-enqueuing the raster, so
            // the next subscriber tick is allowed to flip `content.visible(true)`
            // (PRD-RTE-PARITY-FIX G2 close path).
            const group = node as TextBoxGroup;
            group.__editingActive = false;
            node.findOne<Konva.Shape>('.textbox-content')?.visible(true);
            // Trigger a fresh raster so the bitmap matches any style mutations
            // performed during edit. The lifecycle controller wires
            // `__enqueueRaster`; optional-chain in case of test doubles.
            const obj = this.#state.getObject(id);
            if (obj && isTextBox(obj)) {
                const scale = this.#state.viewport.scale || 1;
                group.__enqueueRaster?.(obj, scale);
            }
            this.#bindings?.redrawContent();
        }
    }

    #removeEditorDom(): void {
        // PRD-RTE-TIPTAP-FIX §RC-C / T3.3: clear the cached selection on
        // every editor teardown. All three edit-exit paths (Tab commit,
        // Escape cancel, click-outside commit via `#dispatchCommit`) route
        // through `#closeEditor` → `#removeEditorDom`, so this single
        // clear-point covers all of them plus `hostDisconnected` and the
        // new-session re-open path in `#openEditor`. Without this, a stale
        // `from/to` could survive into the next textbox edit session
        // (Failure Conditions §"stale cache leak").
        this.#clearCachedSelection();
        // O2D.W-PRD-026 / TR4: tear down the per-edit `viewport-changed`
        // subscription here — the single convergence point of ALL teardown
        // paths (commit/cancel via `#closeEditor`, `hostDisconnected`, and
        // `#openEditor` re-entry) — so it never leaks past edit-exit and never
        // double-subscribes on re-open.
        if (this.#viewportUnsub) {
            this.#viewportUnsub();
            this.#viewportUnsub = null;
        }
        // O2D.W-PRD-032 / TR2 (DEFECT A): tear down the per-edit `object-updated`
        // alignment subscription here — same single convergence point as
        // `#viewportUnsub` — so it never leaks past edit-exit and never
        // double-subscribes on re-open.
        if (this.#objectUpdatedUnsub) {
            this.#objectUpdatedUnsub();
            this.#objectUpdatedUnsub = null;
        }
        // O2D.W-PRD-027 §TR2: detach the live selection-state listener with the
        // SAME handler ref and emit `edit/selection-cleared` so the optionsbar
        // drops the live snapshot when the edit session ends. Done BEFORE
        // `destroy()` while `#editingId` is still set (all teardown paths clear
        // it only AFTER `#removeEditorDom`). `editor.on()` returns the editor,
        // not an unsubscribe fn, so `off(event, handler)` with the stored ref is
        // the only correct detach (Failure Condition FC4 — no leak, no
        // double-subscribe on re-open).
        if (this.#onSelectionChange) {
            if (this.#tiptap) {
                try {
                    this.#tiptap.off('selectionUpdate', this.#onSelectionChange);
                    this.#tiptap.off('update', this.#onSelectionChange);
                } catch {
                    // Defensive: `off` can throw if the view was already torn
                    // down by a concurrent path — swallow so teardown continues.
                }
            }
            const clearedId = this.#editingId;
            if (clearedId != null) {
                this.#bus.uiActions$.next({
                    type: 'edit/selection-cleared',
                    id: objectId(clearedId),
                });
            }
            this.#onSelectionChange = null;
        }
        // Destroy the TipTap instance first so its ProseMirror view is torn
        // down cleanly before the wrapper element leaves the DOM. PRD-RTE-TIPTAP
        // §FC1: exactly one `destroy()` per editor lifetime.
        if (this.#tiptap) {
            try {
                this.#tiptap.destroy();
            } catch {
                // Defensive: TipTap can throw during destroy if the editor was
                // already torn down by a concurrent path — swallow so the
                // wrapper still gets removed.
            }
            this.#tiptap = null;
        }
        this.#editorContent = null;
        const editor = this.#editor;
        if (!editor) return;
        this.#editor = null;
        if (editor.parentNode) editor.remove();
    }

    /**
     * PRD-RTE-TIPTAP-FIX §RC-C / T3.2 — capture the live TipTap selection into
     * `#cachedSelectionFrom/To`. Called from the capturing-phase mousedown
     * handler when an optionsbar element is the click target, before the
     * browser migrates focus and ProseMirror collapses the selection.
     */
    #cacheCurrentSelection(): void {
        const sel = this.#tiptap?.state.selection;
        if (!sel) return;
        this.#cachedSelectionFrom = sel.from;
        this.#cachedSelectionTo = sel.to;
    }

    /**
     * PRD-RTE-TIPTAP-FIX §RC-C / T3.2 — reset the cached selection so a stale
     * range cannot leak into a later `applyFormat` call. Called after each
     * mark application AND in `#removeEditorDom()` (T3.3) so all three
     * edit-exit paths (Tab commit, Escape cancel, click-outside commit)
     * converge on a clean cache before the next edit session opens.
     */
    #clearCachedSelection(): void {
        this.#cachedSelectionFrom = null;
        this.#cachedSelectionTo = null;
    }

    /**
     * PRD-RTE-TIPTAP-FIX §RC-C / T3.3 — call `chain.setTextSelection(...)`
     * with the cached range when both indices are non-null. No-op when the
     * cache is empty, preserving the existing cursor-position toggle
     * semantics for keyboard shortcuts (Ctrl+B / Ctrl+I etc.) that never
     * populate the cache.
     */
    #restoreCachedSelection(chain: ReturnType<Editor['chain']>): void {
        if (this.#cachedSelectionFrom === null || this.#cachedSelectionTo === null) return;
        chain.setTextSelection({
            from: this.#cachedSelectionFrom,
            to: this.#cachedSelectionTo,
        });
    }

    /**
     * O2D.W-PRD-033 §TR1 — apply a VALUED `textStyle` attribute with
     * set-vs-normalize semantics: clicking the value that is already
     * active/present clears it back to normal, otherwise it sets the new value
     * over the range.
     *
     * The `apply-format` bus contract cannot carry this decision (an identical
     * clicked `'super'` / `'uppercase'` cannot say "set" vs "normalize"), so the
     * controller reads its own 3-state selection snapshot and decides here.
     *
     * Anke, 2026-07-22 — generalised from the former `#applyVerticalScript`.
     * "Großbuchstaben"/"Kleinbuchstaben" (`textTransform`) hatten dieselbe
     * Zwei-Zustands-Anforderung, liefen aber über ein blindes `setMark` und
     * ließen sich darum NICHT wieder ausschalten. `verticalScript` löste das
     * längst — jetzt teilen sich beide diesen Weg. Das Ausschalten trifft nur
     * den AKTUELL markierten Bereich (`#normalizeValuedTextStyle` läuft über die
     * jetzige Auswahl), nicht die ganze früher formatierte Passage — Ankes
     * zweite Bedingung fällt damit von selbst ab.
     *
     * @param chain the pre-focused chain (cached selection already restored).
     */
    #applyValuedTextStyle(
        chain: ReturnType<Editor['chain']>,
        attr: 'verticalScript' | 'textTransform',
        clicked: string,
    ): boolean {
        const tiptap = this.#tiptap;
        if (!tiptap) return false;
        // Flush focus + cached-selection restore so BOTH the decision-read and
        // the normalize walk operate on the user's intended range (the click on
        // the optionsbar may have migrated focus and collapsed the live
        // selection).
        chain.run();
        if (this.#isValuedAttrActive(attr, clicked)) {
            this.#normalizeValuedTextStyle(attr, clicked);
        } else {
            tiptap.chain().focus().setMark('textStyle', { [attr]: clicked }).run();
        }
        this.#clearCachedSelection();
        return true;
    }

    /**
     * O2D.W-PRD-033 §TR3 — whether `clicked` is the value of `attr` currently
     * active (uniform ON) or present (mixed) in the selection, read from the
     * same 3-state snapshot the optionsbar consumes. Defensive: an unreadable
     * state (e.g. a recording stub with no real doc) yields `false`, so the set
     * path is taken.
     */
    #isValuedAttrActive(attr: 'verticalScript' | 'textTransform', clicked: string): boolean {
        const s = this.#safeMarkSnapshot()?.[attr];
        if (!s) return false;
        if (s.state === 'on') return s.value === clicked;
        if (s.state === 'mixed') return (s.present ?? []).includes(clicked);
        return false;
    }

    /**
     * Read the current selection snapshot defensively, returning `null` when
     * the editor state is not a real ProseMirror doc (so the recording-stub
     * unit tests cannot throw on the `nodesBetween` read path).
     */
    #safeMarkSnapshot(): SelectionMarkSnapshot | null {
        if (!this.#tiptap) return null;
        try {
            return readSelectionMarkState(this.#tiptap.state);
        } catch {
            return null;
        }
    }

    /**
     * O2D.W-PRD-033 §TR2 — clear a valued `textStyle` attribute back to normal.
     * A collapsed caret clears the STORED mark (no runs to walk); a range emits
     * ONE transaction that, mirroring the READ walk in `selection-mark-state.ts`
     * (`doc.nodesBetween`), clears `attr` on every run whose value equals
     * `clicked` — replacing the run's `textStyle` mark (self-excluding, so
     * `addMark` replaces) while preserving its co-located `color`/`fontSize`.
     *
     * Nur der AKTUELL markierte Bereich (`from…to`) wird angefasst: Hat der
     * Nutzer eine kleinere Passage markiert als die ursprünglich formatierte,
     * bleibt der Rest großgeschrieben — Ankes zweite Bedingung.
     */
    #normalizeValuedTextStyle(attr: 'verticalScript' | 'textTransform', clicked: string): void {
        const tiptap = this.#tiptap;
        if (!tiptap) return;
        const state = tiptap.state;
        const textStyleType = state.schema.marks.textStyle;
        if (state.selection.empty || !textStyleType) {
            tiptap.chain().focus().setMark('textStyle', { [attr]: null }).run();
            return;
        }
        const { from, to } = state.selection;
        const tr = state.tr;
        state.doc.nodesBetween(from, to, (node, pos) => {
            if (!node.isText) return;
            const mark = node.marks.find((m) => m.type.name === 'textStyle');
            if (!mark || mark.attrs[attr] !== clicked) return;
            const runFrom = Math.max(pos, from);
            const runTo = Math.min(pos + node.nodeSize, to);
            tr.addMark(runFrom, runTo, textStyleType.create({ ...mark.attrs, [attr]: null }));
        });
        if (tr.docChanged) {
            tiptap.view.dispatch(tr);
        }
        tiptap.view.focus();
    }

    /**
     * @internal Test-only injection point for the optionsbar-bridge unit test.
     * Stage-3 `#private` fields cannot be reached via index access from
     * outside the class, so the spec needs an explicit hook to swap in a
     * chainable TipTap stub. Not part of the public API; do not call from
     * production code.
     */
    __testInjectTiptap(editor: unknown): void {
        this.#tiptap = editor as Editor;
    }

    /**
     * @internal Test-only read accessor for the live TipTap editor
     * (O2D.W-PRD-030 §TR4 / T3.2). The edit-overlay display-scaling test needs
     * to drive real ProseMirror transactions (set-selection, insert-content) and
     * read the rendered view against the plugin's `Decoration.inline` set. Not
     * part of the public API; do not call from production code.
     */
    __testGetTiptap(): Editor | null {
        return this.#tiptap;
    }

    /**
     * @internal Test-only mirror of `#cacheCurrentSelection()`. The unit test
     * exercises the cache contract without simulating the full DOM mousedown
     * sequence (capturing-phase ordering is verified in T3.2 via live
     * browser, not in vitest).
     */
    __testCacheCurrentSelection(): void {
        this.#cacheCurrentSelection();
    }

    /**
     * @internal Test-only read accessor for the cached selection indices.
     * Returns both fields so the spec can assert both populate-on-capture
     * and clear-on-apply contracts.
     */
    __testGetCachedSelection(): { from: number | null; to: number | null } {
        return { from: this.#cachedSelectionFrom, to: this.#cachedSelectionTo };
    }

    /**
     * 2026-08-15 (Anke, "clean rewrite") — the single lever that replaces the
     * old `#applyScaledFont` + font-scale-decoration-plugin combo. `content`
     * is authored entirely in WORLD UNITS (see `buildTextboxEditContentElement`
     * — font-size, padding, width/height all unscaled, matching the persisted
     * document 1:1); this one `transform: scale(...)` maps that to on-screen
     * pixels for every descendant at once — font-sizes, padding, line-heights,
     * icons — via the browser's own cascade, not hand-multiplied JS. CSS-only
     * by design: it MUST NOT touch the ProseMirror document (no
     * `applyFontSize`/`setMark`), which would steal focus per wheel-tick.
     * Reused by the `viewport-changed` handler (zoom/pan during edit) and
     * `#openEditor` (edit-enter-while-zoomed).
     */
    #applyContentTransform(content: HTMLDivElement): void {
        const scale = this.#state.viewport.scale || 1;
        content.style.transform = `scale(${scale})`;
        content.style.transformOrigin = '0 0';
    }

    #positionEditor(editor: HTMLDivElement, id: ObjectId): void {
        const bbox = this.#bindings?.screenBboxFor(id);
        if (!bbox) return;
        // `bbox` is in host-element coordinates (screenBboxFor adds the
        // host→.board-root offset — at DPR ≠ 1 the host border is sub-pixel,
        // e.g. 0.8 px). The editor is appended INSIDE .board-root, so its
        // absolute position is relative to .board-root — subtract that same
        // offset so the editor lands exactly on the Konva textbox.
        //
        // Query `.board-root` directly (NOT `editor.offsetParent`): this
        // method runs BEFORE the editor is appended to the DOM, so at this
        // point `editor.offsetParent` is still null.
        const boardRoot = this.#host?.querySelector<HTMLElement>('.board-root');
        let offsetX = 0;
        let offsetY = 0;
        if (boardRoot && this.#host) {
            const br = boardRoot.getBoundingClientRect();
            const hr = this.#host.getBoundingClientRect();
            offsetX = br.left - hr.left;
            offsetY = br.top - hr.top;
        }
        editor.style.left = `${bbox.x - offsetX}px`;
        editor.style.top = `${bbox.y - offsetY}px`;
        editor.style.width = `${bbox.width}px`;
        editor.style.height = `${bbox.height}px`;
        // Match the textbox rotation. `bbox` sits at the node's ORIGIN (its
        // top-left corner) — the same point Konva rotates the node around — so
        // rotating the overlay by the same angle around its top-left corner
        // aligns the edit view with the rotated box. Both Konva and CSS use
        // clockwise-positive degrees, so the angle carries over verbatim.
        editor.style.transformOrigin = '0 0';
        editor.style.transform = bbox.rotation ? `rotate(${bbox.rotation}deg)` : '';
    }
}
